/**
 * DashboardDocumentUploadCard.jsx — iter894 (Feb 2026)
 *
 * Compact dashboard tile that mounts the same UnifiedDocumentUpload
 * modal used inside BHI, so users can add labs / epicrises / DUTCH /
 * DNA from a single place across the whole app.
 *
 * User feedback (iter894): "От много места има възможност да се качват
 * лабораторни данни и документи. Много объркано е иначе — искам същия
 * модул на dashboard-а."
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Upload, FileText, FlaskConical, Dna, Stethoscope, CheckCircle2 } from "lucide-react";
import UnifiedDocumentUpload from "@/components/breast_intel/UnifiedDocumentUpload";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { emitHealthEvent } from "@/utils/healthEvents";
import { useHealthEvent } from "@/utils/healthEvents";

export default function DashboardDocumentUploadCard({ isDark }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const bg = language === "bg";
  const [open, setOpen] = useState(false);
  const [lastUpload, setLastUpload] = useState(null);

  // iter894b — fetch most-recent uploaded document to reassure the user
  // that the pipeline processed their previous upload.
  const fetchLastUpload = React.useCallback(async () => {
    if (!API) return;
    try {
      const { data } = await axios.get(`${API}/documents/history?limit=1`);
      const first = (data?.items || [])[0];
      if (first) setLastUpload(first);
    } catch {
      /* silent — chip is best-effort */
    }
  }, [API]);

  useEffect(() => { fetchLastUpload(); }, [fetchLastUpload]);
  // Refresh chip when anything in the pipeline changes
  useHealthEvent("conditions-changed", fetchLastUpload);

  const t = bg
    ? {
        eyebrow: "Точка на истина за твоя Digital Twin",
        title: "Качете здравни документи",
        subtitle:
          "От тук идват диагнозите, лабораторните маркери и активните KEGG пътища в целия ти профил. Лабораторни резултати, епикризи, DUTCH тест, ДНК — AI обработва всичко и опреснява Digital Twin автоматично.",
        cta: "Качи документ",
        chips: ["Лабораторни", "Епикризи", "DUTCH", "ДНК"],
        lastLabel: "Последно",
      }
    : {
        eyebrow: "Source of truth for your Digital Twin",
        title: "Upload health documents",
        subtitle:
          "Diagnoses, lab markers and active KEGG pathways across your profile come from here. Lab reports, epicrises, DUTCH tests, DNA — AI parses everything and refreshes the Digital Twin automatically.",
        cta: "Upload document",
        chips: ["Labs", "Epicrises", "DUTCH", "DNA"],
        lastLabel: "Last",
      };

  const cardBase = isDark
    ? "bg-gradient-to-br from-sky-950/25 via-[hsl(222,47%,10%)] to-cyan-900/15 border-sky-500/25"
    : "bg-gradient-to-br from-sky-50 via-white to-cyan-50 border-sky-200";

  return (
    <>
      <section
        className={`rounded-2xl border p-5 transition-all hover:shadow-md ${cardBase}`}
        data-testid="cc-dashboard-document-upload"
      >
        <div className="flex items-start gap-2 mb-3">
          <Upload
            className={`w-4 h-4 mt-0.5 shrink-0 ${isDark ? "text-sky-300" : "text-sky-600"}`}
          />
          <div className="min-w-0 flex-1">
            {/* iter972 — Eyebrow "Точка на истина за твоя Digital Twin"
                hidden on mobile per user request (redundant with title
                and clutter for small screens). Kept on md+. */}
            <div
              className={`hidden md:block text-[10px] uppercase tracking-wider font-bold ${
                isDark ? "text-sky-300" : "text-sky-700"
              }`}
            >
              {t.eyebrow}
            </div>
            <div className={`text-sm font-extrabold mt-0.5 md:mt-0.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              {t.title}
            </div>
          </div>
        </div>

        <p
          className={`text-xs leading-relaxed mb-4 ${
            isDark ? "text-gray-300" : "text-gray-700"
          }`}
        >
          {t.subtitle}
        </p>

        <div className="grid grid-cols-4 gap-1.5 mb-4">
          {[
            { Icon: FileText,     label: t.chips[0], color: isDark ? "text-sky-300"     : "text-sky-600"     },
            { Icon: Stethoscope,  label: t.chips[1], color: isDark ? "text-amber-300"   : "text-amber-600"   },
            { Icon: FlaskConical, label: t.chips[2], color: isDark ? "text-fuchsia-300" : "text-fuchsia-600" },
            { Icon: Dna,          label: t.chips[3], color: isDark ? "text-indigo-300"  : "text-indigo-600"  },
          ].map(({ Icon, label, color }) => (
            <div
              key={label}
              className={`rounded-lg p-1.5 flex flex-col items-center gap-0.5 border ${
                isDark ? "bg-white/[0.03] border-white/5" : "bg-white/70 border-gray-200"
              }`}
            >
              <Icon className={`w-3.5 h-3.5 ${color}`} />
              <span
                className={`text-[9px] font-semibold ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                {label}
              </span>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="cc-dashboard-document-upload-open"
          className={`w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors ${
            isDark
              ? "bg-sky-500/20 text-sky-100 hover:bg-sky-500/30"
              : "bg-sky-600 text-white hover:bg-sky-700"
          }`}
        >
          <Upload className="w-4 h-4" />
          {t.cta}
        </button>

        {/* iter894b — Confirm previous upload was processed (best-effort chip) */}
        {lastUpload && (
          <div
            className={`mt-2.5 flex items-center gap-1.5 text-[10px] leading-tight ${
              isDark ? "text-emerald-300/90" : "text-emerald-700"
            }`}
            data-testid="cc-dashboard-last-upload-chip"
          >
            <CheckCircle2 className="w-3 h-3 shrink-0" />
            <span>
              {t.lastLabel}: {lastUpload.type_label_bg || lastUpload.filename || lastUpload.type || (bg ? "документ" : "document")}
              {lastUpload.date ? ` · ${new Date(lastUpload.date).toLocaleDateString(bg ? "bg-BG" : "en-US", { day: "numeric", month: "short", year: "numeric" })}` : ""}
            </span>
          </div>
        )}
      </section>

      <UnifiedDocumentUpload
        open={open}
        onClose={() => setOpen(false)}
        lang={language}
        onDone={() => {
          // Bust any cached snapshots so consumers refresh their views.
          emitHealthEvent("conditions-changed", { source: "dashboard-doc-upload" });
          // Immediately refresh the last-upload chip
          fetchLastUpload();
        }}
      />
    </>
  );
}
